<template>
  <transition name="slide-up">
    <div v-if="visible && release" class="release-banner">
      <div class="release-banner__inner">
        <span class="release-banner__icon">📢</span>
        <span v-if="release.title" class="release-banner__title">{{ release.title }}</span>
        <span ref="textWrap" class="release-banner__text-wrap">
          <span
            ref="textNode"
            class="release-banner__text"
            :class="{ 'is-scrolling': isLongText }"
            :title="release.tickerContent"
          >
            {{ release.tickerContent }}
          </span>
        </span>
        <span v-if="hasDetail" class="release-banner__detail-link" @click="showDetail">
          查看详情 >
        </span>
      </div>
      <i class="el-icon-close release-banner__close" title="关闭并不再提醒" @click="handleClose"></i>

      <!-- 版本更新详情弹窗 -->
      <el-dialog
        v-if="detailVisible"
        title="版本发布详情"
        :visible.sync="detailVisible"
        width="520px"
        append-to-body
        custom-class="release-banner-dialog"
      >
        <div class="release-dialog-body">
          <div class="release-dialog-header">
            <span class="release-dialog-tag">{{ release.versionNo }}</span>
            <span class="release-dialog-time" v-if="formattedPublishTime">
              发布时间：{{ formattedPublishTime }}
            </span>
          </div>
          <div class="release-dialog-title">{{ release.tickerContent }}</div>
          <div v-if="hasDetail" class="release-dialog-content">
            <div class="release-dialog-label">更新说明：</div>
            <div class="release-dialog-text release-rich-text" v-html="formattedDetailContent"></div>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" size="small" @click="detailVisible = false">已 阅</el-button>
        </span>
      </el-dialog>
    </div>
  </transition>
</template>

<script>
import { getCurrentReleaseVersion } from '@/fetch/releaseVersion'
import { formatReleaseContent, hasReleaseContent } from '@/utils/releaseContent'

export default {
  name: 'ReleaseTicker',
  data() {
    return {
      release: null,
      visible: false,
      detailVisible: false,
      isLongText: false
    }
  },
  computed: {
    hasDetail() {
      return !!(this.release && hasReleaseContent(this.release.remark || this.release.content))
    },
    formattedDetailContent() {
      return formatReleaseContent(this.release && (this.release.remark || this.release.content))
    },
    formattedPublishTime() {
      if (!this.release) return ''
      if (this.release.status !== 2) return ''
      return this.formatDateTime(this.release.publishTime)
    }
  },
  mounted() {
    this.loadCurrentRelease()
    window.addEventListener('resize', this.checkTextWidth)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkTextWidth)
  },
  methods: {
    loadCurrentRelease() {
      getCurrentReleaseVersion().then(res => {
        const data = res.data || null
        if (data && data.tickerContent) {
          this.release = data
          this.visible = true
          this.$nextTick(() => {
            this.checkTextWidth()
          })
        }
      }).catch(() => {
        this.release = null
        this.visible = false
      })
    },
    handleClose() {
      this.visible = false
    },
    showDetail() {
      this.detailVisible = true
    },
    formatDateTime(value) {
      if (!value) return ''
      if (Array.isArray(value)) {
        const normalized = [
          value[0],
          String(value[1]).padStart(2, '0'),
          String(value[2]).padStart(2, '0'),
          String(value[3] || 0).padStart(2, '0'),
          String(value[4] || 0).padStart(2, '0'),
          String(value[5] || 0).padStart(2, '0')
        ]
        return `${normalized[0]}-${normalized[1]}-${normalized[2]} ${normalized[3]}:${normalized[4]}:${normalized[5]}`
      }
      return String(value).replace('T', ' ').slice(0, 19)
    },
    checkTextWidth() {
      this.$nextTick(() => {
        const wrap = this.$refs.textWrap
        const text = this.$refs.textNode
        if (wrap && text) {
          // If the inner text width exceeds wrapper's width, it should scroll
          this.isLongText = text.offsetWidth > wrap.offsetWidth
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.release-banner {
  box-sizing: border-box;
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  height: 34px;
  width: 480px; /* Fixed capsule width */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 0 16px;
  background: rgba(10, 25, 47, 0.82);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 9999px;
  color: #ffffff;
  backdrop-filter: blur(12px);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 0 12px rgba(0, 212, 255, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(10, 25, 47, 0.9);
    border-color: rgba(0, 212, 255, 0.5);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.15),
      0 0 20px rgba(0, 212, 255, 0.3);
    transform: translate(-50%, -2px);
  }
}

.release-banner__inner {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  margin-right: 12px;
}

.release-banner__icon {
  font-size: 13px;
  margin-right: 6px;
  flex-shrink: 0;
}

.release-banner__title {
  flex-shrink: 0;
  margin-right: 8px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #00f0ff;
  background: rgba(0, 212, 255, 0.15);
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 9999px;
  line-height: 1;
}

.release-banner__text-wrap {
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  position: relative;
  /* Add mask edges for elegant text fade-out at borders */
  mask-image: linear-gradient(to right, transparent, #000 12px, #000 calc(100% - 12px), transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, #000 12px, #000 calc(100% - 12px), transparent);
}

.release-banner__text {
  display: inline-block;
  font-size: 12px;
  color: #ff9e22; /* High-end glowing digital orange */
  text-shadow: 0 0 8px rgba(255, 158, 34, 0.25);
  letter-spacing: 0.2px;
  white-space: nowrap;
  transition: transform 0.2s ease;
}

.release-banner__text.is-scrolling {
  animation: marquee 16s linear infinite;
  padding-left: 100%; /* Start scrolling from the right boundary */
}

@keyframes marquee {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-100%, 0, 0);
  }
}

.release-banner__detail-link {
  flex-shrink: 0;
  margin-left: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #00e6ff;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  text-decoration: none;
  border-bottom: 1px dashed rgba(0, 230, 255, 0.4);
  padding-bottom: 1px;

  &:hover {
    color: #ffffff;
    border-bottom-color: #ffffff;
  }
}

.release-banner__close {
  flex-shrink: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  margin-left: 12px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.15);
  }
}

@media (max-width: 520px) {
  .release-banner {
    width: calc(100vw - 32px);
  }
}

/* Transition effects */
.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-up-enter {
  transform: translate(-50%, 40px) scale(0.95);
  opacity: 0;
}
.slide-up-leave-to {
  transform: translate(-50%, 40px) scale(0.95);
  opacity: 0;
}
</style>

<style lang="scss">
/* 详情弹窗自定义科技感暗色样式 */
.release-banner-dialog {
  background: rgba(12, 28, 52, 0.95) !important;
  border: 1px solid rgba(0, 212, 255, 0.4) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
  border-radius: 8px !important;
  backdrop-filter: blur(12px);

  .el-dialog__title {
    color: #00e6ff !important;
    font-size: 16px;
    font-weight: 600;
  }

  .el-dialog__headerbtn .el-dialog__close {
    color: rgba(255, 255, 255, 0.7) !important;
    &:hover {
      color: #00e6ff !important;
    }
  }

  .el-dialog__body {
    color: #ffffff;
    padding: 16px 20px;
  }

  .release-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .release-dialog-tag {
    background: rgba(0, 212, 255, 0.2);
    color: #00f0ff;
    border: 1px solid rgba(0, 240, 255, 0.5);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
  }

  .release-dialog-time {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
  }

  .release-dialog-title {
    font-size: 14px;
    font-weight: 500;
    color: #ffffff;
    line-height: 1.5;
    margin-bottom: 14px;
  }

  .release-dialog-content {
    background: rgba(0, 15, 30, 0.5);
    border: 1px solid rgba(0, 212, 255, 0.15);
    border-radius: 6px;
    padding: 12px;
  }

  .release-dialog-label {
    font-size: 12px;
    color: rgba(0, 230, 255, 0.85);
    margin-bottom: 6px;
  }

  .release-dialog-text {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.75;
    word-break: break-word;

    p {
      margin: 0 0 10px;
    }

    p:last-child {
      margin-bottom: 0;
    }

    ol,
    ul {
      margin: 8px 0 12px 20px;
      padding-left: 16px;
    }

    li {
      margin-bottom: 8px;
      line-height: 1.75;
    }

    h1,
    h2,
    h3,
    h4 {
      margin: 14px 0 8px;
      color: #ffffff;
      line-height: 1.5;
    }

    strong,
    b {
      color: #ffffff;
    }
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(0, 212, 255, 0.15);
    padding: 10px 20px 15px;

    .el-button--primary {
      background: linear-gradient(135deg, #0099cc 0%, #00d4ff 100%) !important;
      border: none !important;
      font-weight: 500;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}
</style>
